import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useFetchSPageMutation } from "@/features/auth/api/authAPI";
import { SPageResponse } from "@/features/auth/types/auth";
import styles from "./SPageView.module.scss";
import Loader from "@/shared/ui/Loader/Loader";
import SPagesApps from "./SPagesApps";

const SPageView = () => {
  const { slug } = useParams();
  const [fetchSPage, { isLoading: isLoadingSPage }] = useFetchSPageMutation();

  const [sPage, setSPage] = useState<SPageResponse | null>(null);

  useEffect(() => {
    if (slug) {
      fetchSPage({ slug }).then((resp) => {
        if (resp.data) {
          setSPage(resp.data);
        }
      });
    }
  }, [slug, fetchSPage]);

  // Add metadata useEffect
  useEffect(() => {
    if (sPage && sPage.keyword && sPage.slug) {
      const pageKeyword = sPage.keyword;
      const pageSlug = sPage.slug;
      const pageDescription =
        sPage.attrs?.paragraph ||
        `Discover ${pageKeyword} app designs and UI patterns. Explore innovative mobile app interfaces and user experience strategies on ScreensDesign.`;

      const calculatedPageUrl = `https://screensdesign.com/categories/${pageSlug}`;
      const calculatedPageTitle = `${pageKeyword} | Mobile App Design Inspiration | ScreensDesign`;
      const calculatedMetaDescription =
        pageDescription.length > 160
          ? pageDescription.substring(0, 157) + "..."
          : pageDescription;

      const calculatedOgTitle = calculatedPageTitle;
      const calculatedOgDescription =
        pageDescription.length > 300
          ? pageDescription.substring(0, 297) + "..."
          : pageDescription;

      // Enhanced helper function to handle both existing and new tags
      const setTag = (
        tagName: "meta" | "link",
        attributeKey: "name" | "property" | "rel",
        attributeValue: string,
        contentKey: "content" | "href",
        contentValue: string,
      ) => {
        // First try to find existing tag with the correct attribute
        let selector = `${tagName}[${attributeKey}='${attributeValue}']`;
        let tag = document.querySelector<HTMLMetaElement | HTMLLinkElement>(
          selector,
        );

        // For Twitter cards, also check for incorrect 'property' attribute from index.html
        if (
          !tag &&
          attributeKey === "name" &&
          attributeValue.startsWith("twitter:")
        ) {
          const incorrectSelector = `${tagName}[property='${attributeValue}']`;
          tag = document.querySelector<HTMLMetaElement | HTMLLinkElement>(
            incorrectSelector,
          );
          if (tag) {
            // Remove the incorrect attribute and set the correct one
            tag.removeAttribute("property");
            tag.setAttribute("name", attributeValue);
          }
        }

        // For LinkedIn cards, remove them as they don't exist (LinkedIn uses OG tags)
        if (attributeValue.startsWith("linkedin:")) {
          const linkedinTag = document.querySelector(
            `meta[property='${attributeValue}']`,
          );
          if (linkedinTag) {
            linkedinTag.remove();
          }
          return; // Don't create LinkedIn tags
        }

        if (!tag) {
          tag = document.createElement(tagName);
          tag.setAttribute(attributeKey, attributeValue);
          document.head.appendChild(tag);
        }
        tag.setAttribute(contentKey, contentValue);
      };

      // Manage Document Title
      document.title = calculatedPageTitle;

      // Manage Meta Description (standard)
      setTag(
        "meta",
        "name",
        "description",
        "content",
        calculatedMetaDescription,
      );

      // Manage Canonical Link
      setTag("link", "rel", "canonical", "href", calculatedPageUrl);

      // Manage Open Graph (OG) Tags
      setTag("meta", "property", "og:url", "content", calculatedPageUrl);
      setTag("meta", "property", "og:title", "content", calculatedOgTitle);
      setTag(
        "meta",
        "property",
        "og:description",
        "content",
        calculatedOgDescription,
      );
      setTag("meta", "property", "og:type", "content", "website");
      setTag("meta", "property", "og:site_name", "content", "ScreensDesign");
      setTag(
        "meta",
        "property",
        "og:image",
        "content",
        "https://screensdesign.com/img/Preview.jpg?v=2",
      );

      // Manage Twitter Card Tags (will fix the incorrect 'property' attributes from index.html)
      setTag("meta", "name", "twitter:card", "content", "summary_large_image");
      setTag("meta", "name", "twitter:url", "content", calculatedPageUrl);
      setTag("meta", "name", "twitter:title", "content", calculatedOgTitle);
      setTag(
        "meta",
        "name",
        "twitter:description",
        "content",
        calculatedOgDescription,
      );
      setTag(
        "meta",
        "name",
        "twitter:image",
        "content",
        "https://screensdesign.com/img/Preview.jpg?v=2",
      );

      // Remove LinkedIn tags (they don't exist - LinkedIn uses OG tags)
      setTag("meta", "property", "linkedin:card", "content", "");
      setTag("meta", "property", "linkedin:url", "content", "");
      setTag("meta", "property", "linkedin:title", "content", "");
      setTag("meta", "property", "linkedin:description", "content", "");
      setTag("meta", "property", "linkedin:image", "content", "");
    }
  }, [sPage]); // Re-run this effect when sPage changes

  return (
    <div className={styles.container}>
      {isLoadingSPage || !sPage ? (
        <div className={styles.loading}>
          <Loader size={40} />
        </div>
      ) : (
        <SPagesApps sPage={sPage} />
      )}
    </div>
  );
};

export default SPageView;
